import { Controller } from "@hotwired/stimulus";
import moment from "moment";
import * as echarts from "echarts";

export default class extends Controller {
  static targets = ["chartBody"];

  connect() {
    this.noticesData = null;
    this.deploysData = null;

    this.initializeChart();
  }

  initializeChart() {
    if (!this.hasChartBodyTarget) {
      return;
    }
    const colorPref =
      document.documentElement.getAttribute("data-bs-theme") === "dark"
        ? "dark"
        : "light";

    this.chart =
      echarts.getInstanceByDom(this.chartBodyTarget) ||
      echarts.init(this.chartBodyTarget);
    this.chart.setOption({
      legend: {
        data: ["Deploys"],
        orient: "horizontal",
        icon: "circle",
        textStyle: {
          color: colorPref == "light" ? "#333" : "#E2DBDD",
        },
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          label: {
            formatter: function (params) {
              return moment(params.value).format("dddd, MMM D, h:mm a");
            },
          },
        },
      },
      toolbox: {
        showTitle: false,
        tooltip: {
          show: true,
          formatter: (params) => {
            return params.title;
          },
        },
        feature: {
          brush: {
            type: ["lineX"],
            title: {
              lineX: "Select range",
            },
          },
        },
      },
      xAxis: {
        type: "time",
        min: "dataMin",
        max: "dataMax",
        minInterval: 60 * 1000,
      },
      yAxis: {
        type: "value",
      },
      series: [
        {
          name: "Notices",
          type: "bar",
          smooth: true,
          color: ["#00a8a5"],
          barWidth: "99%",
        },
        {
          name: "Deploys",
          type: "scatter",
          color: "red",
          symbol: "none",
          markLine: {
            symbol: "none",
            label: {
              normal: {
                show: false,
              },
              emphasis: {
                backgroundColor: "#FFFFFF",
                padding: 3,
                show: true,
              },
            },
          },
        },
      ],
      grid: {
        containLabel: true,
        left: "0",
        right: "5",
        top: "30",
        bottom: "5",
      },
      brush: {
        throttleType: "debounce",
        throttleDelay: 500,
      },
    });

    // When a user clicks on a single notice bar.
    this.chart.on("click", (params) => {
      if (params.seriesName != "Notices") {
        return;
      }

      let minIndex = params.dataIndex;
      let minValue = this.noticesData[minIndex];
      let maxValue = this.noticesData[minIndex + 1];
      let min = moment.utc(Math.round(minValue[0]));
      let max = null;

      // If this is the last data index of the series, we set the max to the
      // current time.
      if (maxValue) {
        max = moment.utc(Math.round(maxValue[0]));
      } else {
        max = moment.utc();
      }

      if (min && max) {
        this.selection(min, max);
      }
    });

    // When a user uses the range selection tool.
    this.chart.on("brushSelected", (params) => {
      let seriesComponent = params.batch[0].selected[0];

      if (seriesComponent) {
        let minIndex = seriesComponent.dataIndex.at(0);
        let maxIndex = seriesComponent.dataIndex.at(-1);

        // If the selection is too small, the min and max index might be the
        // same. In this case we go to the next index.
        if (minIndex == maxIndex) {
          maxIndex = minIndex + 1;
        }

        let minValue = this.noticesData[minIndex];
        let maxValue = this.noticesData[maxIndex];

        if (minValue && maxValue) {
          let min = moment.utc(Math.round(minValue[0]));
          let max = null;

          // If there is no max value, we set to the current time.
          if (maxValue) {
            max = moment.utc(Math.round(maxValue[0]));
          } else {
            max = moment.utc();
          }

          if (min && max) {
            this.selection(min, max);
          }
        }
      }
    });
  }

  redrawChart(e) {
    if (!this.chart) {
      return;
    }

    this.noticesData = e.detail.data.notices;
    this.deploysData = e.detail.data.deploys;

    // We capture the max number of notices so that we can generate markline
    // data for each deploy. The markline data renders the vertical dashed
    // line using two sets of coordinates for each deploy. We add one for cases
    // where no notice data is present, we still want to render the deploy line.
    let maxNoticeValue = Math.max(...this.noticesData.map((n) => n[1]));
    maxNoticeValue = maxNoticeValue == 0 ? 1 : maxNoticeValue;

    let deploysMarkLineData = this.deploysData.map((deployData) => {
      return [
        { coord: [deployData[0], 0], name: `Rev: ${deployData[2]}` },
        { coord: [deployData[0], maxNoticeValue] },
      ];
    });

    this.chart.setOption({
      series: [
        { name: "Notices", data: this.noticesData },
        {
          name: "Deploys",
          data: this.deploysData,
          markLine: { data: deploysMarkLineData },
        },
      ],
    });

    this.chart.dispatchAction({
      type: "brush",
      command: "clear",
      areas: [],
    });
  }

  resizeChart() {
    this.chart.resize();
  }

  selection(min, max) {
    document.dispatchEvent(
      new CustomEvent("notice-timeline-selection", {
        detail: {
          start: min,
          end: max,
        },
      }),
    );
  }
}
